import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Button, Link, Paper, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import ChooseList from '@/components/choose-list';
import AppButton from '@/components/app-button';
import QuestionImage from '@/components/question-image';
import QuestionProgressDots from '@/screens/main/components/question-progress-dots';
import CorrectAnswer from '../../daily-tipper/question/correct-answer';
import InCorrectAnswer from '../../daily-tipper/question/incorrect-answer';
import { useOnboardingQuestionState } from './useOnboardingQuestionState';

type QuestionsParams = {
  questionId: string;
};

const Questions: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { questionId } = useParams<QuestionsParams>();

  const {
    answerId,
    chooseAnswer,
    currentQuestion,
    error,
    getDotVariant,
    hasAnswer,
    index,
    isCorrect,
    isInCorrect,
    loading,
    nextLink,
    options,
    questionAnswerForView,
    setAnswerLoading,
    submitAnswer,
    userQuestions,
  } = useOnboardingQuestionState({ questionId });

  const questionNumberNode =
    index >= 0 ? t('onboard.quiz.cme-examle', { number: index + 1 }) : <Skeleton width={100} />;

  const questionNode = currentQuestion?.value ?? (
    <>
      <Skeleton />
      <Skeleton />
      <Skeleton />
    </>
  );

  const optionsNode = currentQuestion
    ? options
    : [...Array(4)].map(() => ({
        value: null,
        label: <Skeleton />,
      }));

  const nextAnswerNode =
    index < 0 ? <>&nbsp;</> : index < userQuestions.length - 1 ? t('common.next') : t('common.finish');
  const setAnswerNode = t('common.submit-answer');

  if (error) {
    console.error('QUESTION_QUERY', error);
    return <Link href="/onboard-final">{t('onboard.questions.error-finish')}</Link>;
  }

  return (
    <Box
      clone
      data-cy-dt-question
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      minHeight="-webkit-fill-available"
    >
      <Paper elevation={0}>
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            py={5}
            px={4}
            mx={-4}
            borderBottom="1px solid #E6EEFA"
          >
            <QuestionProgressDots
              items={userQuestions}
              getItemId={(item) => String(item.id)}
              getItemVariant={getDotVariant}
              columnGap={10}
            />
            <Typography variant="overline">{questionNumberNode}</Typography>
          </Box>
          <Box paddingBottom={3} mt={5}>
            <Typography variant="subtitle1">{questionNode}</Typography>
          </Box>
          <QuestionImage src={currentQuestion?.questionImage ?? undefined} alt={currentQuestion?.value} />
          {!hasAnswer && (
            <ChooseList
              data-cy-answers
              options={optionsNode}
              value={answerId}
              onChange={chooseAnswer}
            />
          )}
          {questionAnswerForView && isCorrect && (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            <CorrectAnswer userQuestion={questionAnswerForView as any} />
          )}
          {questionAnswerForView && isInCorrect && (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            <InCorrectAnswer userQuestion={questionAnswerForView as any} />
          )}
        </Box>
        <Box py={7}>
          {!loading && !hasAnswer && (
            <AppButton
              fullWidth
              data-cy-submit-answer-btn
              color="primary"
              variant="contained"
              disabled={!answerId}
              loading={setAnswerLoading}
              onClick={submitAnswer}
              data-dd-action-name="DT set answer"
            >
              {setAnswerNode}
            </AppButton>
          )}
          {!loading && hasAnswer && (
            <Button
              fullWidth
              data-cy-submit-answer-next
              color="primary"
              variant="contained"
              onClick={() => navigate(nextLink, { replace: true })}
            >
              {nextAnswerNode}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Questions;
